import { CadastroRapidoDeColaborador } from '@/components/cadastro-rapido';
import { useMemo } from 'react';
import {
  Badge,
  Button,
  Combobox,
  Field,
  IconClose,
  cn,
} from '@/design-system';

/**
 * Seleção de pessoas e departamentos.
 *
 * Usado em dois lugares com semânticas diferentes — participantes da reunião e
 * destinatários da circular — porque o CONTROLE é o mesmo e o texto é que muda.
 * Duplicar a lista removível em dois arquivos produziria duas acessibilidades
 * diferentes para o mesmo gesto.
 *
 * ACESSIBILIDADE
 *  · a contagem vai num `role="status"`: quem usa leitor de tela ouve
 *    "4 participantes" ao adicionar, sem reler a lista inteira;
 *  · cada item removível é um botão com nome próprio ("Remover Maria Silva dos
 *    participantes"), nunca um "x" solto;
 *  · o tipo do alvo (colaborador × departamento) é TEXTO num `<Badge>`, não um
 *    ícone: em preto e branco os dois ícones ficam indistinguíveis.
 */

export interface PeopleOption {
  readonly id: string;
  readonly name: string;
  readonly hint?: string | undefined;
}

export interface PeoplePickerProps {
  readonly userIds: readonly string[];
  readonly onUserIdsChange: (ids: string[]) => void;
  readonly users: readonly PeopleOption[];
  /** Ausente = o seletor não oferece departamentos. */
  readonly departmentIds?: readonly string[];
  readonly onDepartmentIdsChange?: (ids: string[]) => void;
  readonly departments?: readonly PeopleOption[];
  readonly disabled?: boolean;
  /** "participantes", "destinatários"… Entra nos rótulos e nos anúncios. */
  readonly noun: string;
  readonly userFieldLabel: string;
  readonly userFieldDescription?: string;
  readonly departmentFieldLabel?: string;
  readonly departmentFieldDescription?: string;
  readonly error?: string | undefined;
  readonly emptyHint?: string;
  readonly className?: string;
}

export function PeoplePicker({
  userIds,
  onUserIdsChange,
  users,
  departmentIds,
  onDepartmentIdsChange,
  departments,
  disabled = false,
  noun,
  userFieldLabel,
  userFieldDescription,
  departmentFieldLabel = 'Adicionar departamento',
  departmentFieldDescription,
  error,
  emptyHint,
  className,
}: PeoplePickerProps) {
  const supportsDepartments = Boolean(departments && onDepartmentIdsChange);
  /*
   * Memoizado porque `?? []` produz um array NOVO a cada render, e esta lista é
   * dependência de dois `useMemo` abaixo — sem identidade estável, os dois
   * recalculavam sempre e a memoização não valia nada.
   */
  const selectedDepartmentIds = useMemo(() => departmentIds ?? [], [departmentIds]);

  const selectedUsers = useMemo(
    () =>
      userIds.map((id) => ({
        id,
        name: users.find((user) => user.id === id)?.name ?? 'Colaborador removido',
      })),
    [userIds, users],
  );

  const selectedDepartments = useMemo(
    () =>
      selectedDepartmentIds.map((id) => ({
        id,
        name: departments?.find((item) => item.id === id)?.name ?? 'Departamento removido',
      })),
    [selectedDepartmentIds, departments],
  );

  const availableUsers = useMemo(
    () =>
      users
        .filter((user) => !userIds.includes(user.id))
        .map((user) => ({ value: user.id, label: user.name, hint: user.hint })),
    [users, userIds],
  );

  const availableDepartments = useMemo(
    () =>
      (departments ?? [])
        .filter((item) => !selectedDepartmentIds.includes(item.id))
        .map((item) => ({ value: item.id, label: item.name })),
    [departments, selectedDepartmentIds],
  );

  const total = userIds.length + selectedDepartmentIds.length;

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      <div className={cn('grid gap-4', supportsDepartments && 'lg:grid-cols-2')}>
        <Field label={userFieldLabel} description={userFieldDescription} error={error}>
          <Combobox
            items={availableUsers}
            value={null}
            disabled={disabled}
            onValueChange={(id) => {
              if (id) onUserIdsChange([...userIds, id]);
            }}
            placeholder="Buscar colaborador…"
            criar={{
              rotulo: 'Novo colaborador',
              dialogo: (props) => <CadastroRapidoDeColaborador {...props} />,
            }}
            emptyMessage="Nenhum colaborador disponível."
          />
        </Field>

        {supportsDepartments ? (
          <Field label={departmentFieldLabel} description={departmentFieldDescription}>
            <Combobox
              items={availableDepartments}
              value={null}
              disabled={disabled}
              onValueChange={(id) => {
                if (id && onDepartmentIdsChange) {
                  onDepartmentIdsChange([...selectedDepartmentIds, id]);
                }
              }}
              placeholder="Buscar departamento…"
              emptyMessage="Nenhum departamento disponível."
            />
          </Field>
        ) : null}
      </div>

      <div>
        <p role="status" className="text-sm font-medium text-content">
          {total === 0
            ? `Nenhum ${singular(noun)} definido`
            : `${total} ${total === 1 ? singular(noun) : noun}`}
        </p>

        {total === 0 ? (
          <p className="mt-1 text-sm text-content-muted">
            {emptyHint ?? `Use os campos acima para escolher os ${noun}.`}
          </p>
        ) : (
          <ul aria-label={`Lista de ${noun}`} className="mt-2 flex flex-wrap gap-1.5">
            {selectedDepartments.map((department) => (
              <PersonChip
                key={`department-${department.id}`}
                kindLabel="Departamento"
                label={department.name}
                disabled={disabled}
                removeLabel={`Remover o departamento ${department.name} dos ${noun}`}
                onRemove={() =>
                  onDepartmentIdsChange?.(
                    selectedDepartmentIds.filter((id) => id !== department.id),
                  )
                }
              />
            ))}
            {selectedUsers.map((user) => (
              <PersonChip
                key={`user-${user.id}`}
                kindLabel="Colaborador"
                label={user.name}
                disabled={disabled}
                removeLabel={`Remover ${user.name} dos ${noun}`}
                onRemove={() => onUserIdsChange(userIds.filter((id) => id !== user.id))}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function PersonChip({
  kindLabel,
  label,
  disabled,
  removeLabel,
  onRemove,
}: {
  readonly kindLabel: string;
  readonly label: string;
  readonly disabled: boolean;
  readonly removeLabel: string;
  readonly onRemove: () => void;
}) {
  return (
    <li>
      <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface-sunken py-0.5 pl-2.5 pr-1 text-sm text-content">
        <Badge tone="outline" size="sm">
          {kindLabel}
        </Badge>
        <span className="min-w-0 truncate">{label}</span>
        <Button
          size="icon"
          variant="ghost"
          disabled={disabled}
          iconLabel={removeLabel}
          onClick={onRemove}
          className="h-5 w-5"
        >
          <IconClose size={12} />
        </Button>
      </span>
    </li>
  );
}

/**
 * "participantes" → "participante", "destinatários" → "destinatário".
 *
 * Regra deliberadamente mínima: só remove o `s` final. Os únicos substantivos
 * passados a este componente são esses dois, e uma flexão genérica de português
 * seria mais código do que valor — e erraria em casos que não existem aqui.
 */
function singular(noun: string): string {
  return noun.endsWith('s') ? noun.slice(0, -1) : noun;
}
